import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import {
    DatasetComponent,
    GridComponent,
    LegendComponent,
    ToolboxComponent,
    TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { FiInfo } from 'react-icons/fi';
import { Card } from 'react-bootstrap';

echarts.use([
    DatasetComponent,
    TooltipComponent,
    ToolboxComponent,
    GridComponent,
    LegendComponent,
    BarChart,
    CanvasRenderer,
    UniversalTransition
]);

// Componente para gráfica comparativa anual
const ComparisonChart = ({
    selectedVendor,
    yearlyData,
    onVendorChange,
    vendedores,
    startYear: startYearProp,
    endYear: endYearProp,
    onRangeChange
}) => {
    // Rango de años seleccionable (2..6)
    const currentYear = new Date().getFullYear();
    
    // Estados locales con valores por defecto de 6 años
    const defaultStartYear = currentYear - 5; // 6 años hacia atrás
    const defaultEndYear = currentYear;
    
    const [localStartYear, setLocalStartYear] = useState(defaultStartYear);
    const [localEndYear, setLocalEndYear] = useState(defaultEndYear);
    const [rangeMsg, setRangeMsg] = useState('');

    const controlled = typeof startYearProp === 'number' && typeof endYearProp === 'number';
    const startYear = controlled ? startYearProp : localStartYear;
    const endYear = controlled ? endYearProp : localEndYear;

    // Opciones de años: 2020 .. año actual
    const yearsOptions = useMemo(() => {
        const list = [];
        for (let y = 2020; y <= currentYear; y++) list.push(y);
        return list;
    }, [currentYear]);

    // Normaliza el rango para garantizar 2..6 años (mínimo 2 años diferentes, máximo 6)
    const normalizeRange = (s, e) => {
        if (e < s) [s, e] = [e, s];
        let span = e - s + 1;
        // Mínimo 2 años diferentes para comparación
        if (span < 2) e = s + 1;
        // Máximo 6 años
        if (span > 6) s = e - 5;
        if (s < 2020) s = 2020;
        if (e > currentYear) e = currentYear;
        return [s, e];
    };
    
    const vendorComparisonOption = useMemo(() => {
        if (!Array.isArray(yearlyData)) {
            return {
                title: {
                    text: 'Comparativa de Ventas por Vendedor',
                    left: 'center',
                    top: '15px',
                    textStyle: {
                        fontSize: 18,
                        fontWeight: 'bold',
                        color: '#374151'
                    }
                },
                series: []
            };
        }

        // Procesar los datos que llegan como array de objetos
        const data = yearlyData;

        // Función para procesar datos por vendedor y año
        const processDataByVendor = (data, vendorFilter) => {
            let filteredData;

            if (vendorFilter === 'TODOS') {
                // Para TODOS, usar todos los datos y acumular por año
                filteredData = data;
            } else {
                // Para vendedor específico
                filteredData = data.filter(item => item.vNombre === vendorFilter || item.vUsuario === vendorFilter);
            }

            // Agrupar por año y sumar totales
            const yearlyTotals = {};
            filteredData.forEach(item => {
                const year = item.year ?? item.año; // compatibilidad si aún llega 'año'
                if (!yearlyTotals[year]) {
                    yearlyTotals[year] = 0;
                }
                yearlyTotals[year] += item.totalMXN || 0;
            });

            // Limitar por rango seleccionado
            const [s, e] = normalizeRange(startYear, endYear);
            const yearsInRange = [];
            for (let y = s; y <= e; y++) yearsInRange.push(y);

            const values = yearsInRange.map(y => yearlyTotals[y] || 0);

            return { years: yearsInRange, values };
        };

        const colors = [
            '#5B9BD5', // 2020 - Azul claro
            '#FFC000', // 2021 - Amarillo
            '#A349A4', // 2022 - Morado
            '#70AD47', // 2023 - Verde
            '#E07C24', // 2024 - Naranja  
            '#4472C4'  // 2025 - Azul
        ];

        const processed = processDataByVendor(data, selectedVendor);
        const years = processed.years;
        const values = processed.values;

        const barData = values.map((value, index) => ({
            value: value,
            itemStyle: {
                color: colors[index % colors.length],
                borderRadius: [2, 2, 0, 0]
            }
        }));

        const seriesName = selectedVendor === 'TODOS' ? 'TODOS LOS VENDEDORES' : selectedVendor;

        const seriesData = [{
            name: seriesName,
            type: 'bar',
            data: barData,
            emphasis: {
                itemStyle: {
                    shadowBlur: 10,
                    shadowColor: 'rgba(0,0,0,0.2)'
                }
            },
            barWidth: '60%'
        }];

        const legendData = [seriesName];
        const showLegend = false;

        return {
            animation: false,
            title: {
                text: 'Comparativa de venta anual',
                left: 'left',
                top: '10px',
                textStyle: {
                    fontSize: 16,
                    fontWeight: 'bold',
                    color: '#374151'
                }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                },
                backgroundColor: '#ffffff',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                borderRadius: 8,
                padding: [12, 16],
                textStyle: {
                    color: '#374151',
                    fontSize: 13
                },
                formatter: function (params) {
                    const formatCurrency = (value) => {
                        return new Intl.NumberFormat('es-MX', {
                            style: 'currency',
                            currency: 'MXN',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                        }).format(value);
                    };

                    let result = `<div style="font-weight: 600; color: #1f2937; margin-bottom: 3px; display: flex; align-items: center; gap: 6px;">
                        📅 <span>${params[0].name}</span>
                    </div>`;
                    
                    params.forEach(param => {
                        result += `<div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                            <span style="color: #6b7280; font-weight: 500;">${param.seriesName}</span>
                            <span style="font-weight: 700; color: #1f2937;">${formatCurrency(param.value)}</span>
                        </div>`;
                    });
                    
                    return result;
                }
            },
            legend: {
                data: legendData,
                top: '10px',
                right: 'right',
                textStyle: {
                    fontSize: 12
                },
                show: showLegend
            },
            grid: {
                left: '5%',
                right: '5%',
                bottom: '5%',
                top: '12%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: years,
                axisLabel: {
                    fontSize: 11,
                    color: '#666666'
                },
                axisLine: {
                    show: false
                },
                axisTick: {
                    show: false
                }
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: function (value) {
                        return value.toLocaleString();
                    },
                    fontSize: 11,
                    color: '#666666'
                },
                axisLine: {
                    show: false
                },
                axisTick: {
                    show: false
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: '#E5E5E5',
                        width: 1,
                        type: 'solid'
                    }
                }
            },
            series: seriesData
        };
    }, [selectedVendor, yearlyData, vendedores, startYear, endYear]);

    return (
        <Card className="rounded-lg shadow-md p-4 w-full mt-4">
            <div className="row mb-3">
                {/* Select de Vendedores */}
                <div className="col-md-4">
                    <div className="form-group">
                        <label className="form-label text-uppercase small fw-medium mb-1">
                            Vendedores
                        </label>
                        <select
                            value={selectedVendor}
                            onChange={(e) => onVendorChange(e.target.value)}
                            className="form-select form-select-sm"
                        >
                            {vendedores.map(vendor => (
                                <option key={vendor} value={vendor}>
                                    {vendor === 'TODOS' ? 'TODOS LOS VENDEDORES' : vendor}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Rango de Años */}
                <div className="col-md-8 mt-3 mt-md-0">
                    <div className="form-group">
                        <label className="form-label text-uppercase small fw-medium mb-1 d-flex align-items-center">
                            Seleccionar Rango de Tiempo
                            <FiInfo
                                title={`Rango permitido: 2 a 6 años para comparación. Rango actual: ${Math.abs(endYear - startYear) + 1} años`}
                                className="text-muted ms-2"
                            />
                        </label>
                        <div className="d-flex align-items-center gap-1 gap-sm-2 flex-wrap">
                            <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                                <label className="form-label mb-0 fw-medium text-dark d-none d-sm-inline small">Desde</label>
                                <label className="form-label mb-0 fw-medium text-dark d-sm-none small">De</label>
                                <select
                                    className="form-select form-select-sm"
                                    style={{ width: '100px' }}
                                    value={startYear}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value, 10);
                                        const candidateSpan = Math.abs(val - endYear) + 1;
                                        if (candidateSpan > 6) {
                                            setRangeMsg('Máximo 6 años. Se ajustó automáticamente.');
                                        } else {
                                            setRangeMsg('');
                                        }
                                        const [s, e2] = normalizeRange(val, endYear);
                                        if (controlled) {
                                            onRangeChange && onRangeChange(s, e2);
                                        } else {
                                            setLocalStartYear(s);
                                            setLocalEndYear(e2);
                                        }
                                    }}
                                >
                                    {yearsOptions.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                                <label className="form-label mb-0 fw-medium text-dark d-none d-sm-inline small">Hasta</label>
                                <label className="form-label mb-0 fw-medium text-dark d-sm-none small">A</label>
                                <select
                                    className="form-select form-select-sm"
                                    style={{ width: '100px' }}
                                    value={endYear}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value, 10);
                                        const candidateSpan = Math.abs(startYear - val) + 1;
                                        if (candidateSpan > 6) {
                                            setRangeMsg('Máximo 6 años. Se ajustó automáticamente.');
                                        } else {
                                            setRangeMsg('');
                                        }
                                        const [s, e2] = normalizeRange(startYear, val);
                                        if (controlled) {
                                            onRangeChange && onRangeChange(s, e2);
                                        } else {
                                            setLocalStartYear(s);
                                            setLocalEndYear(e2);
                                        }
                                    }}
                                >
                                    {yearsOptions.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mensaje de error */}
            {rangeMsg && (
                <div className="row">
                    <div className="col-12">
                        <small className="text-danger">{rangeMsg}</small>
                    </div>
                </div>
            )}

            <ReactEChartsCore
                key={`comparison-chart-${selectedVendor}-${startYear}-${endYear}`}
                echarts={echarts}
                option={vendorComparisonOption}
                style={{ height: '500px', width: '100%' }}
                notMerge={true}
                replaceMerge={['series', 'legend', 'tooltip']}
            />
        </Card>
    );
};

ComparisonChart.propTypes = {
    selectedVendor: PropTypes.string.isRequired,
    yearlyData: PropTypes.arrayOf(
        PropTypes.shape({
            year: PropTypes.number.isRequired,
            idUsuario: PropTypes.number.isRequired,
            totalMXN: PropTypes.number.isRequired,
            vNombre: PropTypes.string.isRequired,
            vUsuario: PropTypes.string.isRequired
        })
    ).isRequired,
    onVendorChange: PropTypes.func.isRequired,
    vendedores: PropTypes.arrayOf(PropTypes.string).isRequired,
    startYear: PropTypes.number,
    endYear: PropTypes.number,
    onRangeChange: PropTypes.func
};

export default ComparisonChart;